import { useState } from "react";
import { Link } from "react-router-dom";
import { useBranding, brandLabel } from "@/context/BrandingContext";
import { LogoMonogram } from "@/components/Logo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { CONTAINER, HEADER_MARK } from "../tokens";

// Un lien interne passe par le routeur ; une ancre pure (« #contact ») ou
// un lien externe reste un <a>. Défini au niveau du module, pas dans le
// composant : une fonction recréée à chaque rendu remonterait ses enfants
// pour rien.
const NavLink = ({ url, className, children, onClick }) =>
  url.startsWith("#") || url.startsWith("http") ? (
    <a href={url} className={className} onClick={onClick}>{children}</a>
  ) : (
    <Link to={url} className={className} onClick={onClick}>{children}</Link>
  );

// En-tête du handoff : bandeau COLLANT (et non fixe — d'où l'absence de
// padding compensatoire dans Layout.jsx pour ce moteur), fond ivoire
// translucide flouté, filet bas. Sous 780px les liens laissent place à un
// burger qui déplie un panneau vertical, fermé au clic sur un lien.
//
// Sur mobile la barre tient sur UNE ligne, jamais deux : le retour à la
// ligne (`flex-wrap`) n'est autorisé qu'à partir de `md`, où il sert au menu
// desktop quand il compte beaucoup d'entrées. Sous `md`, logo + sélecteur de
// langue + « Devis gratuit » + burger ne rentrent pas dans 360-400px avec
// des écarts de 30px : le bouton d'appel et le burger passaient à la ligne,
// alignés à gauche sous le logo. Sous `sm` le bouton d'appel quitte donc la
// barre et rejoint le panneau déroulant du burger — même règle que
// l'en-tête partagé (components/Header.jsx), dont le CTA est aussi masqué
// sous `sm` et repris dans le tiroir. Le sélecteur de langue reste dans la
// barre : un visiteur qui ne lit pas la langue affichée doit pouvoir en
// changer sans deviner ce que cache un burger.
//
// Marge automatique en `me-auto` (logique) et non `mr-auto` (physique) : elle
// sépare le logo du reste de la barre. En français elle vaut exactement la
// même chose qu'avant — margin-right. En arabe (dir="rtl", module
// multilingue), une marge physique à droite creusait un vide ENTRE le logo et
// le bord droit, décollant le logo du bord et tassant le menu contre lui,
// alors que le bouton d'appel restait seul à l'autre extrémité. La version
// logique bascule du bon côté et reflète la maquette au lieu de la casser.
//
// À défaut de logo téléversé (Studio de thème > Identité de marque), on
// pose le SYMBOLE DE MARQUE (LogoMonogram, voir public/brand/) plutôt que
// l'initiale de la boutique dans un carré : c'est le vrai logo, et il n'y a
// qu'un seul dessin à maintenir. Il est en revanche recoloré à la palette
// du thème — le bleu de marque jurerait sur l'ivoire de cette maquette,
// alors que la FORME, elle, reste la même partout.
//
// Couleurs : fond et textes suivent les couleurs de ZONE « En-tête » du
// Studio (header-bg / header-text, voir tailwind.config.js), qui retombent
// sur `cream` / `ink` tant que l'admin n'a rien choisi.
export default function SiteHeader({ props = {}, blocks = [] }) {
  const branding = useBranding();
  const [open, setOpen] = useState(false);
  const items = (blocks || [])
    .filter((b) => b.type === "menu_item" && b.enabled !== false && b.props?.label && b.props?.url)
    .map((b) => ({ id: b.id, label: b.props.label, url: b.props.url }));
  // Muet tant que l'identité de la boutique n'est pas connue — même règle
  // que Logo.jsx : sinon le nom par défaut ("ClickExpert") apparaîtrait une
  // fraction de seconde sur une boutique qui porte un autre nom. Les
  // dimensions du monogramme sont fixes, la ligne ne bouge donc pas.
  const name = branding.loaded ? branding.name || "" : "";
  // Texte à côté du logo : réglé dans le Studio, distinct du nom de la
  // boutique (qui reste le nom accessible du lien).
  const label = branding.loaded ? brandLabel(branding) : "";
  // Style d'en-tête (Studio > En-tête > Style d'en-tête) : "classic" (barre
  // du handoff, logo à gauche) ou "stacked" — sur desktop, logo seul et
  // centré sur une première rangée, puis menu, langue et bouton d'appel
  // centrés sur une seconde rangée sous un filet. Sur mobile, les deux
  // styles donnent la même barre compacte.
  const stacked = props.header_layout === "stacked";
  const ctaClass = "items-center font-display font-medium text-[14.5px] text-header-bg bg-header-text py-2.5 px-[18px] rounded-[9px] whitespace-nowrap hover:opacity-90 transition-opacity";
  const navLinks = items.map((it) => (
    <NavLink key={it.id} url={it.url} className="hover:text-header-text transition-colors duration-150">
      {it.label}
    </NavLink>
  ));

  return (
    <header className="sticky top-0 z-40 backdrop-blur-[14px] bg-header-bg/[0.86] border-b border-header-text/[0.09]">
      <div className={`${CONTAINER} py-[15px] flex items-center gap-3 sm:gap-5 md:gap-[30px] ${stacked ? "md:justify-center" : "md:flex-wrap"}`}>
        {/* `min-w-0` + `truncate` : sur mobile c'est le NOM de la boutique qui
            cède la place, jamais les commandes à droite (langue, burger). */}
        <Link to="/" className={`flex items-center gap-2.5 me-auto min-w-0 ${stacked ? "md:me-0" : ""}`} aria-label={name}>
          {branding.logo_url ? (
            <img src={branding.logo_url} alt="" className="h-[27px] w-auto max-w-[140px] shrink-0 object-contain" />
          ) : (
            <LogoMonogram
              className="h-[27px] w-[27px] shrink-0"
              bg="rgb(var(--c-blush, 182 230 48))"
              mark={HEADER_MARK}
            />
          )}
          {!branding.hide_name && (
            <span className="font-display font-semibold text-[17px] tracking-[-0.01em] text-header-text truncate">{label}</span>
          )}
        </Link>

        {!stacked && (
          <nav className="hidden md:flex gap-[26px] flex-wrap text-[14.5px] text-header-text/[0.66]">
            {navLinks}
          </nav>
        )}

        {/* Module multilingue : ne rend rien tant qu'il n'est pas activé sur
            le thème — cet en-tête est donc inchangé pour toute boutique qui
            ne s'en sert pas. En "stacked", il rejoint la seconde rangée sur
            desktop. */}
        <LanguageSwitcher className={`shrink-0 ${stacked ? "md:hidden" : ""}`} linkClass="text-header-text/[0.66] hover:text-header-text" />

        {props.cta_label && (
          <NavLink
            url={props.cta_url || "#contact"}
            className={`hidden sm:inline-flex ${stacked ? "md:hidden" : ""} ${ctaClass}`}
          >
            {props.cta_label}
          </NavLink>
        )}

        {/* Burger dès qu'il y a quelque chose à déplier : des liens, ou
            seulement le bouton d'appel masqué sous `sm`. */}
        {(items.length > 0 || props.cta_label) && (
          <button
            type="button"
            aria-label="Menu"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="md:hidden shrink-0 h-[42px] w-[42px] border-control border-header-text/20 rounded-[10px] flex flex-col items-center justify-center gap-[5px]"
          >
            <span className="w-[18px] h-[1.5px] bg-header-text" />
            <span className="w-[18px] h-[1.5px] bg-header-text" />
            <span className="w-[18px] h-[1.5px] bg-header-text" />
          </button>
        )}
      </div>

      {stacked && (items.length > 0 || props.cta_label) && (
        <div className="hidden md:block border-t border-header-text/[0.09]">
          <nav
            className={`${CONTAINER} py-3 flex flex-wrap items-center justify-center gap-x-[26px] gap-y-2 text-[14.5px] text-header-text/[0.66]`}
            aria-label="Navigation principale"
          >
            {navLinks}
            <LanguageSwitcher className="shrink-0" linkClass="text-header-text/[0.66] hover:text-header-text" />
            {props.cta_label && (
              <NavLink url={props.cta_url || "#contact"} className={`inline-flex ${ctaClass}`}>
                {props.cta_label}
              </NavLink>
            )}
          </nav>
        </div>
      )}

      {open && (
        <div className="md:hidden flex flex-col gap-1 pt-1.5 px-[18px] pb-5 border-t border-header-text/10">
          {items.map((it, i) => (
            <NavLink
              key={it.id}
              url={it.url}
              onClick={() => setOpen(false)}
              className={`text-header-text text-[16px] py-3 ${i === items.length - 1 ? "" : "border-b border-header-text/[0.08]"}`}
            >
              {it.label}
            </NavLink>
          ))}
          {/* Reprise du bouton d'appel, uniquement là où la barre ne l'affiche
              pas (sous `sm`) : au-dessus il est déjà visible, inutile de le
              montrer deux fois. */}
          {props.cta_label && (
            <NavLink
              url={props.cta_url || "#contact"}
              onClick={() => setOpen(false)}
              className="sm:hidden mt-3 inline-flex items-center justify-center font-display font-medium text-[15px] text-header-bg bg-header-text py-3 px-[18px] rounded-[9px] hover:opacity-90 transition-opacity"
            >
              {props.cta_label}
            </NavLink>
          )}
        </div>
      )}
    </header>
  );
}
